/**
 * CommonLinksAdmin.tsx — Admin > Common Links.
 *
 * The corporate dashboard's Common Links page for the signed-in account: the
 * links on users' dashboards, in the Lead Sources layout (see
 * components/admin/AdminList.tsx):
 *   - Title (its description under it, cut short), URL (opens in a new tab)
 *     and Visibility (Everyone / Admin Only), in the account's order until a
 *     title is clicked; search by title or URL; Show deleted;
 *   - "Add link" top right; a click on a live row opens it; Edit / Delete on a
 *     live row, Restore on a deleted one;
 *   - corp's form: Title *, URL *, Content / Description and Visibility, with
 *     no image (as corp: a new link saves a blank image, and an edit leaves a
 *     link's existing image alone);
 * and from legacy's page (admin_dashboard_list.php), which corp left out:
 *   - the order: in the account's order (no column sorted, no search, deleted
 *     links hidden) each row has a grip; a drop saves the new order at once.
 *
 * On a Locked page (useAdminPageLevel) there is no Add, no grip (nor its
 * column), no row icon and no Actions column; a live link still opens, as a
 * viewer.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ExternalLink, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { ADMIN_DRAG_COLUMN, SortableAdminRow, useAdminDragSensors } from '@/components/admin/SortableAdminRow';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { getErrorMessage } from '@/services/apiClient';
import {
  LINK_VISIBILITY_OPTIONS,
  createCommonLink,
  deleteCommonLink,
  listAdminCommonLinks,
  reorderCommonLinks,
  restoreCommonLink,
  updateCommonLink,
  type AdminCommonLink,
} from '@/services/commonLinks.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'title', header: 'Title', sortable: true },
  { key: 'url', header: 'URL', sortable: true },
  { key: 'visibility', header: 'Visibility', sortable: true, className: 'w-32' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function linkTitle(l: AdminCommonLink): string {
  return l.entryTitle.trim() || `Link #${l.dashboardEntryID}`;
}

function visibilityLabel(status: number): string {
  return LINK_VISIBILITY_OPTIONS.find((o) => o.value === status)?.label ?? 'Everyone';
}

/** Content may hold HTML; the list shows its text only. */
function plainText(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

const SORT_COMPARE: ListSortCompare<AdminCommonLink> = {
  title: (a, b) => compareText(linkTitle(a), linkTitle(b)),
  url: (a, b) => compareText(a.entryURL, b.entryURL),
  visibility: (a, b) => a.status - b.status,
};

export function CommonLinksAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [links, setLinks] = useState<AdminCommonLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminCommonLink | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminCommonLink | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setLinks(await listAdminCommonLinks());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load common links'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      links.filter(
        (l) =>
          (showDeleted || l.active) &&
          (query === '' || l.entryTitle.toLowerCase().includes(query) || l.entryURL.toLowerCase().includes(query)),
      ),
    [links, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: null, compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });
  // Reordering needs the account's order of every live link on screen, and an Editable page.
  const canDrag = !sorting.sort && query === '' && !showDeleted && !locked;
  const sensors = useAdminDragSensors();

  async function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const from = rows.findIndex((l) => l.dashboardEntryID === active.id);
    const to = rows.findIndex((l) => l.dashboardEntryID === over.id);
    if (from < 0 || to < 0) return;
    const before = links;
    const moved = arrayMove(rows, from, to).map((l, i) => ({ ...l, order: i + 1 }));
    setLinks([...moved, ...links.filter((l) => !l.active)]);
    try {
      await reorderCommonLinks(moved.map((l) => l.dashboardEntryID));
    } catch (err) {
      setLinks(before);
      toast.error(getErrorMessage(err, 'The new order could not be saved.'));
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    const { dashboardEntryID } = deleting;
    try {
      await deleteCommonLink(dashboardEntryID);
      setLinks((prev) => prev.map((l) => (l.dashboardEntryID === dashboardEntryID ? { ...l, active: false } : l)));
      toast.success('Link deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The link could not be deleted.'));
    }
  }

  async function handleRestore(l: AdminCommonLink) {
    try {
      await restoreCommonLink(l.dashboardEntryID);
      setLinks((prev) => prev.map((x) => (x.dashboardEntryID === l.dashboardEntryID ? { ...x, active: true } : x)));
      toast.success('Link restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The link could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Common Links"
      subtitle="Manage common links shown on user dashboards."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add link
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search links..."
          searchLabel="Search links"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="common-links-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading common links…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No links found' : 'No links yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a link to get started.'}
        />
      ) : (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : canDrag ? [ADMIN_DRAG_COLUMN, ...COLUMNS] : COLUMNS} aria-label="Common links" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            <SortableContext items={pagination.pageItems.map((l) => l.dashboardEntryID)} strategy={verticalListSortingStrategy}>
            {pagination.pageItems.map((l) => {
              const deleted = !l.active;
              const title = linkTitle(l);
              const content = plainText(l.entryContent);
              return (
                <SortableAdminRow
                  key={l.dashboardEntryID}
                  id={l.dashboardEntryID}
                  title={title}
                  canDrag={canDrag}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(l)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <div className="flex items-center gap-3">
                      <div className="min-w-0">
                        <AdminRowName deleted={deleted} nameTestId="common-link-title">
                          {title}
                        </AdminRowName>
                        {content && <div className="mt-0.5 max-w-md truncate text-xs text-slate-400">{content}</div>}
                      </div>
                    </div>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    {l.entryURL && (
                      <a
                        href={l.entryURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex max-w-xs items-center gap-1 text-blue-600 hover:underline"
                      >
                        <span className="truncate">{l.entryURL}</span>
                        <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
                      </a>
                    )}
                  </td>
                  <td className={`${ADMIN_TD_CLASS} text-slate-600`}>{visibilityLabel(l.status)}</td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${title}`} onClick={() => void handleRestore(l)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${title}`} onClick={() => setEditing(l)} />
                            <AdminRowAction kind="delete" label={`Delete ${title}`} onClick={() => setDeleting(l)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </SortableAdminRow>
              );
            })}
            </SortableContext>
          </AdminListTable>
          </DndContext>
          <PaginationFooter pagination={pagination} noun={['link', 'links']} />
        </>
      )}

      {editing && (
        <CommonLinkFormModal
          key={editing === 'new' ? 'new' : editing.dashboardEntryID}
          link={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${linkTitle(deleting)}"?` : ''}
        message="It will no longer show on user dashboards. You can restore it from Show deleted."
        confirmLabel="Delete link"
        variant="danger"
      />
    </AdminListPage>
  );
}

function CommonLinkFormModal({
  link,
  onClose,
  onSaved,
}: {
  link: AdminCommonLink | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = link === null;
  const t = useTrackedForm(
    () => ({
      entryTitle: link?.entryTitle ?? '',
      entryURL: link?.entryURL ?? '',
      entryContent: link?.entryContent ?? '',
      status: link?.status ?? 0,
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const missingTitle = t.form.entryTitle.trim() === '';
  const missingUrl = t.form.entryURL.trim() === '';
  const errors = [
    ...(missingTitle ? ['Title is required'] : []),
    ...(missingUrl ? ['URL is required'] : []),
  ];

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    try {
      const data = {
        entryTitle: t.form.entryTitle.trim(),
        entryURL: t.form.entryURL.trim(),
        entryContent: t.form.entryContent,
        status: t.form.status,
      };
      // No image field (corp parity): an edit leaves the link's image alone, a new link has none.
      if (link) await updateCommonLink(link.dashboardEntryID, data);
      else await createCommonLink({ ...data, entryImage: '' });
      toast.success(isNew ? 'Link created' : 'Link updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The link could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Link' : 'Edit Link'}
      isNew={isNew}
      dirty={isNew ? Object.values(t.form).some((v) => v !== '' && v !== 0) : t.dirty}
      saving={saving}
      errors={errors}
      saveLabel={isNew ? 'Create' : 'Save'}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
    >
      <div className="grid gap-4">
        <DialogField
          label="Title"
          htmlFor="common-link-title"
          required
          state={missingTitle ? 'error' : 'none'}
          labelHint={missingTitle ? '— required' : undefined}
          badge={t.badge('entryTitle', 'Title')}
        >
          <Input
            id="common-link-title"
            value={t.form.entryTitle}
            placeholder="Link title"
            maxLength={255}
            onChange={(e) => t.patch({ entryTitle: e.target.value })}
            className={fieldClasses.input(missingTitle ? 'error' : 'none')}
          />
        </DialogField>
        <DialogField
          label="URL"
          htmlFor="common-link-url"
          required
          state={missingUrl ? 'error' : 'none'}
          labelHint={missingUrl ? '— required' : undefined}
          badge={t.badge('entryURL', 'URL')}
        >
          <Textarea
            id="common-link-url"
            rows={2}
            value={t.form.entryURL}
            placeholder="https://..."
            maxLength={2000}
            onChange={(e) => t.patch({ entryURL: e.target.value })}
            className={fieldClasses.input(missingUrl ? 'error' : 'none')}
          />
        </DialogField>
        <DialogField label="Content / Description" htmlFor="common-link-content" badge={t.badge('entryContent', 'Content / Description')}>
          <Textarea autoResize
            id="common-link-content"
            rows={4}
            value={t.form.entryContent}
            placeholder="Optional description or HTML content..."
            onChange={(e) => t.patch({ entryContent: e.target.value })}
          />
        </DialogField>
        <DialogField label="Visibility" htmlFor="common-link-visibility" badge={t.badge('status', 'Visibility')}>
          <Select<number>
            id="common-link-visibility"
            options={LINK_VISIBILITY_OPTIONS}
            value={t.form.status}
            onChange={(v) => t.patch({ status: v ?? 0 })}
          />
        </DialogField>
      </div>
    </AdminFormModal>
  );
}

